import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  TextInput,
  Clipboard,
  Platform,
  Dimensions,
} from 'react-native';
import Icon from '@expo/vector-icons/MaterialIcons';
import { collection, getDocs, doc, updateDoc, query, orderBy, Timestamp, limit, startAfter, QueryDocumentSnapshot, DocumentData, getCountFromServer, where } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { fonts } from '../utils/fonts';

const lightColors = {
  white: '#FFFFFF',
  deepNavy: '#1a1d23',
  lightGray: '#F5F7FA',
  warmGray: '#6B7280',
  beeYellow: '#F4C430',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
};

const darkColors = {
  ...lightColors,
  white: '#1A1D23',
  deepNavy: '#E5E7EB',
  lightGray: '#0F1114',
  warmGray: '#9CA3AF',
};

interface PaymentRequest {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  userPhone: string;
  subscriptionId: string;
  subscriptionName: string;
  amount: number;
  paymentPeriod: string;
  bankId: string;
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  paymentReference: string;
  status: 'pending' | 'confirmed' | 'rejected';
  createdAt: any;
  updatedAt: any;
}

const PaymentRequestsContent: React.FC<{ colors?: any; isDarkMode?: boolean }> = ({
  colors = lightColors,
  isDarkMode = false,
}) => {
  const [payments, setPayments] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);
  const [copiedReference, setCopiedReference] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));
  const isMobile = dimensions.width < 768;

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setDimensions(window));
    return () => sub?.remove();
  }, []);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [firstDoc, setFirstDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageCache, setPageCache] = useState<Map<number, {
    payments: PaymentRequest[];
    firstDoc: QueryDocumentSnapshot<DocumentData>;
    lastDoc: QueryDocumentSnapshot<DocumentData>;
  }>>(new Map());

  useEffect(() => {
    // Clear page cache when filter changes
    console.log(`🔄 Filter changed to: ${filter}, clearing page cache...`);
    setPageCache(new Map());
    setCurrentPage(1);
    fetchPayments();
  }, [filter]);

  useEffect(() => {
    // Fetch when page changes (but not on initial load)
    if (currentPage > 1) {
      console.log(`📄 Page changed to: ${currentPage}`);
      fetchPayments();
    }
  }, [currentPage]);

  useEffect(() => {
    fetchTotalCount();
  }, [filter]);

  const fetchTotalCount = async () => {
    try {
      const countQuery = filter === 'all'
        ? query(collection(db, 'paymentRequests'))
        : query(collection(db, 'paymentRequests'), where('status', '==', filter));
      
      const snapshot = await getCountFromServer(countQuery);
      setTotalCount(snapshot.data().count);
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  const fetchPayments = async (direction: 'next' | 'prev' | 'first' = 'first') => {
    try {
      setLoading(true);
      console.log(`🔄 Fetching payments - Filter: ${filter}, Page: ${currentPage}, Direction: ${direction}`);

      // Don't use cache - always fetch fresh data
      console.log('⚠️ Skipping page cache to ensure fresh data');

      let paymentsQuery;
      
      if (filter === 'all') {
        paymentsQuery = query(
          collection(db, 'paymentRequests'),
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        );
      } else {
        paymentsQuery = query(
          collection(db, 'paymentRequests'),
          where('status', '==', filter),
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        );
      }

      // Add pagination cursor
      if (direction === 'next' && lastDoc) {
        if (filter === 'all') {
          paymentsQuery = query(
            collection(db, 'paymentRequests'),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(pageSize)
          );
        } else {
          paymentsQuery = query(
            collection(db, 'paymentRequests'),
            where('status', '==', filter),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(pageSize)
          );
        }
      }

      console.log('🌐 Fetching from Firestore with forceRefresh...');
      // Force refresh to bypass cache - payment requests should always show latest data
      const snapshot = await getDocs(paymentsQuery, { forceRefresh: true, ttl: 30000 }); // 30 second cache
      
      console.log(`✅ Received ${snapshot.docs.length} payment documents from Firestore`);
      
      if (snapshot.empty) {
        console.log('📭 No payments found');
        setPayments([]);
        setFirstDoc(null);
        setLastDoc(null);
        setLoading(false);
        return;
      }

      const paymentsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure required fields have defaults
          status: data.status || 'pending',
          userName: data.userName || 'Unknown',
          userEmail: data.userEmail || '',
          userPhone: data.userPhone || '',
        };
      }) as PaymentRequest[];

      console.log('📊 Payment data sample:', paymentsData.slice(0, 3).map(p => ({
        id: p.id,
        status: p.status,
        userName: p.userName,
        amount: p.amount,
        subscriptionName: p.subscriptionName
      })));

      const newFirstDoc = snapshot.docs[0];
      const newLastDoc = snapshot.docs[snapshot.docs.length - 1];

      setPayments(paymentsData);
      setFirstDoc(newFirstDoc);
      setLastDoc(newLastDoc);

    } catch (error) {
      console.error('Error fetching payments:', error);
      Alert.alert('Error', 'Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (paymentId: string, paymentData: PaymentRequest) => {
    // Use window.confirm for web compatibility
    const isWeb = typeof window !== 'undefined' && window.confirm;
    
    const confirmAction = async () => {
      try {
        setProcessingPayment(paymentId);
        await updateDoc(doc(db, 'paymentRequests', paymentId), {
          status: 'confirmed',
          updatedAt: new Date(),
        });
        // Clear cache and refetch
        setPageCache(new Map());
        setCurrentPage(1);
        await fetchPayments();
        await fetchTotalCount();
        
        if (isWeb) {
          alert('Payment confirmed successfully. User will be notified via email.');
        } else {
          Alert.alert('Success', 'Payment confirmed successfully. User will be notified via email.');
        }
      } catch (error) {
        console.error('Error confirming payment:', error);
        if (isWeb) {
          alert('Failed to confirm payment');
        } else {
          Alert.alert('Error', 'Failed to confirm payment');
        }
      } finally {
        setProcessingPayment(null);
      }
    };

    if (isWeb) {
      const confirmed = window.confirm(
        `Are you sure you want to confirm payment for ${paymentData.userName}?\n\nThis will activate their ${paymentData.subscriptionName} subscription.`
      );
      if (confirmed) {
        await confirmAction();
      }
    } else {
      Alert.alert(
        'Confirm Payment',
        `Are you sure you want to confirm payment for ${paymentData.userName}?\n\nThis will activate their ${paymentData.subscriptionName} subscription.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Confirm',
            style: 'default',
            onPress: confirmAction,
          },
        ]
      );
    }
  };

  const handleRejectPayment = async (paymentId: string, paymentData: PaymentRequest) => {
    // Use window.confirm for web compatibility
    const isWeb = typeof window !== 'undefined' && window.confirm;
    
    const rejectAction = async () => {
      try {
        setProcessingPayment(paymentId);
        await updateDoc(doc(db, 'paymentRequests', paymentId), {
          status: 'rejected',
          updatedAt: new Date(),
        });
        // Clear cache and refetch
        setPageCache(new Map());
        setCurrentPage(1);
        await fetchPayments();
        await fetchTotalCount();
        
        if (isWeb) {
          alert('Payment rejected. User will be notified via email.');
        } else {
          Alert.alert('Success', 'Payment rejected. User will be notified via email.');
        }
      } catch (error) {
        console.error('Error rejecting payment:', error);
        if (isWeb) {
          alert('Failed to reject payment');
        } else {
          Alert.alert('Error', 'Failed to reject payment');
        }
      } finally {
        setProcessingPayment(null);
      }
    };

    if (isWeb) {
      const confirmed = window.confirm(
        `Are you sure you want to reject payment for ${paymentData.userName}?`
      );
      if (confirmed) {
        await rejectAction();
      }
    } else {
      Alert.alert(
        'Reject Payment',
        `Are you sure you want to reject payment for ${paymentData.userName}?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Reject',
            style: 'destructive',
            onPress: rejectAction,
          },
        ]
      );
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    let date: Date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return 'Invalid Date';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredPayments = searchQuery === ''
    ? payments
    : payments.filter((payment) => {
        const query = searchQuery.toLowerCase();
        return (
          payment.userName.toLowerCase().includes(query) ||
          payment.userEmail.toLowerCase().includes(query) ||
          payment.paymentReference.toLowerCase().includes(query)
        );
      });

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    if (newPage === currentPage) return;
    
    const direction = newPage > currentPage ? 'next' : 'prev';
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilter: 'all' | 'pending' | 'confirmed' | 'rejected') => {
    setFilter(newFilter);
    setCurrentPage(1);
    setPageCache(new Map());
    setLastDoc(null);
    setFirstDoc(null);
  };

  const handleCopyReference = (reference: string) => {
    Clipboard.setString(reference);
    setCopiedReference(reference);
    setTimeout(() => setCopiedReference(null), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#F59E0B';
      case 'confirmed':
        return '#10B981';
      case 'rejected':
        return '#EF4444';
      default:
        return '#94A3B8';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return 'hourglass-empty';
      case 'confirmed':
        return 'check-circle';
      case 'rejected':
        return 'cancel';
      default:
        return 'help';
    }
  };

  const stats = {
    total: payments.length,
    pending: payments.filter((p) => p.status === 'pending').length,
    confirmed: payments.filter((p) => p.status === 'confirmed').length,
    rejected: payments.filter((p) => p.status === 'rejected').length,
    totalRevenue: payments
      .filter((p) => p.status === 'confirmed')
      .reduce((sum, p) => sum + p.amount, 0),
  };

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.adminPageBg, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color="#10B981" />
        <Text style={{ fontSize: 13, color: colors.adminMuted, marginTop: 12, fontFamily: fonts.regular }}>Loading payment requests...</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.adminPageBg }}>
      {/* ── Page Header ── */}
      <View style={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.adminHeaderBg,
        paddingHorizontal: isMobile ? 16 : 28,
        paddingVertical: isMobile ? 14 : 18,
        gap: isMobile ? 10 : 0,
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${colors.sidebarAccent}22`, justifyContent: 'center', alignItems: 'center' }}>
            <Icon name="payment" size={20} color={colors.sidebarAccent} />
          </View>
          <View>
            <Text style={{ fontSize: isMobile ? 16 : 18, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB', letterSpacing: -0.3 }}>Payment Requests</Text>
            {!isMobile && <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: '#64748B', marginTop: 1 }}>Manage and verify payment requests</Text>}
          </View>
        </View>
        <TouchableOpacity
          onPress={() => { setPageCache(new Map()); setCurrentPage(1); fetchPayments(); fetchTotalCount(); }}
          style={{
            flexDirection: 'row', alignItems: 'center', gap: 6,
            paddingHorizontal: 14, paddingVertical: 8, borderRadius: 9,
            backgroundColor: colors.sidebarAccent,
            alignSelf: isMobile ? 'stretch' : 'auto',
            justifyContent: 'center',
          }}
        >
          <Icon name="refresh" size={16} color="#FFFFFF" />
          <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: '#FFFFFF' }}>Refresh</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 24 }}>
        {/* ── Stats Grid ── */}
        {isMobile ? (
          <View style={{ paddingHorizontal: 14, paddingTop: 16, paddingBottom: 12 }}>
            {/* Row 1: Total + Pending */}
            <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
              {[
                { label: 'Total', value: stats.total, color: '#6366F1', icon: 'payment' as const },
                { label: 'Pending', value: stats.pending, color: '#F59E0B', icon: 'hourglass-empty' as const },
              ].map((stat, idx) => (
                <View key={idx} style={{ flex: 1, backgroundColor: colors.adminCardBg, borderTopWidth: 3, borderTopColor: stat.color, borderRadius: 12, padding: 12 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View>
                      <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</Text>
                      <Text style={{ fontSize: 22, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginTop: 2 }}>{stat.value}</Text>
                    </View>
                    <View style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: `${stat.color}15`, justifyContent: 'center', alignItems: 'center' }}>
                      <Icon name={stat.icon} size={16} color={stat.color} />
                    </View>
                  </View>
                </View>
              ))}
            </View>
            {/* Row 2: Confirmed + Rejected */}
            <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
              {[
                { label: 'Confirmed', value: stats.confirmed, color: '#10B981', icon: 'check-circle' as const },
                { label: 'Rejected', value: stats.rejected, color: '#EF4444', icon: 'cancel' as const },
              ].map((stat, idx) => (
                <View key={idx} style={{ flex: 1, backgroundColor: colors.adminCardBg, borderTopWidth: 3, borderTopColor: stat.color, borderRadius: 12, padding: 12 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View>
                      <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</Text>
                      <Text style={{ fontSize: 22, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginTop: 2 }}>{stat.value}</Text>
                    </View>
                    <View style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: `${stat.color}15`, justifyContent: 'center', alignItems: 'center' }}>
                      <Icon name={stat.icon} size={16} color={stat.color} />
                    </View>
                  </View>
                </View>
              ))}
            </View>
            {/* Row 3: Revenue full-width */}
            <View style={{ backgroundColor: colors.adminCardBg, borderTopWidth: 3, borderTopColor: '#8B5CF6', borderRadius: 12, padding: 12 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View>
                  <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Revenue</Text>
                  <Text style={{ fontSize: 18, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginTop: 2 }}>{stats.totalRevenue.toLocaleString()} ETB</Text>
                </View>
                <View style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#8B5CF615', justifyContent: 'center', alignItems: 'center' }}>
                  <Icon name="account-balance-wallet" size={16} color="#8B5CF6" />
                </View>
              </View>
            </View>
          </View>
        ) : (
          <View style={{ flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingTop: 20, paddingBottom: 14, flexWrap: 'wrap' }}>
            {[
              { label: 'Total', value: stats.total, color: '#6366F1', icon: 'payment' as const },
              { label: 'Pending', value: stats.pending, color: '#F59E0B', icon: 'hourglass-empty' as const },
              { label: 'Confirmed', value: stats.confirmed, color: '#10B981', icon: 'check-circle' as const },
              { label: 'Rejected', value: stats.rejected, color: '#EF4444', icon: 'cancel' as const },
              { label: 'Revenue', value: `${stats.totalRevenue.toLocaleString()} ETB`, color: '#8B5CF6', icon: 'account-balance-wallet' as const }
            ].map((stat, idx) => (
              <View key={idx} style={{ flex: 1, minWidth: 160, backgroundColor: colors.adminCardBg, borderTopWidth: 3, borderTopColor: stat.color, borderRadius: 12, padding: 14, ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 } }) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View>
                    <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</Text>
                    <Text style={{ fontSize: idx === 4 ? 16 : 24, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginTop: 4 }}>{stat.value}</Text>
                  </View>
                  <View style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: `${stat.color}15`, justifyContent: 'center', alignItems: 'center' }}>
                    <Icon name={stat.icon} size={18} color={stat.color} />
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* ── Filter Chips + Search ── */}
        <View style={{ paddingHorizontal: isMobile ? 14 : 20, paddingBottom: 14 }}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 10 }} contentContainerStyle={{ gap: 8, paddingRight: 4 }}>
            {(['all', 'pending', 'confirmed', 'rejected'] as const).map((f) => {
              const chipColors: Record<string, string> = { all: '#6366F1', pending: '#F59E0B', confirmed: '#10B981', rejected: '#EF4444' };
              const accent = chipColors[f];
              return (
                <TouchableOpacity key={f} onPress={() => handleFilterChange(f)} style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, backgroundColor: filter === f ? accent : colors.adminBorder }}>
                  <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: filter === f ? '#FFFFFF' : colors.adminMuted, textTransform: 'capitalize' }}>{f}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.adminCardBg, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: colors.adminBorder }}>
            <Icon name="search" size={18} color={colors.adminMuted} />
            <TextInput style={{ flex: 1, marginLeft: 8, fontSize: 13, fontFamily: fonts.regular, color: colors.sidebarTextActive, outlineStyle: 'none' } as any} placeholder="Search by name, email, or reference..." placeholderTextColor={colors.adminMuted} value={searchQuery} onChangeText={setSearchQuery} />
          </View>
        </View>

        {/* ── Content: Cards (mobile) or Table (desktop) ── */}
        {filteredPayments.length === 0 ? (
          <View style={{ margin: isMobile ? 14 : 20, padding: 40, alignItems: 'center', backgroundColor: colors.adminCardBg, borderRadius: 14 }}>
            <Icon name="receipt-long" size={48} color={colors.adminMuted} />
            <Text style={{ fontSize: 14, color: colors.adminMuted, marginTop: 12, fontFamily: fonts.regular }}>No payment requests found</Text>
          </View>
        ) : isMobile ? (
          /* ── Mobile Cards ── */
          <View style={{ paddingHorizontal: 14, gap: 10 }}>
            {filteredPayments.map((payment) => {
              const statusColor = getStatusColor(payment.status);
              return (
                <View
                  key={payment.id}
                  style={{
                    backgroundColor: colors.adminCardBg,
                    borderRadius: 14,
                    borderLeftWidth: 4,
                    borderLeftColor: statusColor,
                    overflow: 'hidden',
                  }}
                >
                  {/* Card Header: user + status badge */}
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', padding: 14, paddingBottom: 10 }}>
                    <View style={{ flex: 1, marginRight: 10 }}>
                      <Text style={{ fontSize: 14, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.sidebarTextActive }}>{payment.userName}</Text>
                      <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted, marginTop: 2 }} numberOfLines={1}>{payment.userEmail}</Text>
                      {!!payment.userPhone && (
                        <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>{payment.userPhone}</Text>
                      )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 20, backgroundColor: `${statusColor}20` }}>
                      <Icon name={getStatusIcon(payment.status)} size={11} color={statusColor} />
                      <Text style={{ fontSize: 10, fontFamily: fonts.bold, fontWeight: '700', color: statusColor, textTransform: 'uppercase', letterSpacing: 0.4 }}>{payment.status}</Text>
                    </View>
                  </View>

                  {/* Divider */}
                  <View style={{ height: 1, backgroundColor: colors.adminBorder, marginHorizontal: 14 }} />

                  {/* Details grid */}
                  <View style={{ padding: 14, paddingTop: 10, gap: 7 }}>
                    {/* Subscription + Amount */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Icon name="stars" size={13} color={colors.adminMuted} />
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{payment.subscriptionName}</Text>
                      </View>
                      <Text style={{ fontSize: 14, fontFamily: fonts.bold, fontWeight: '700', color: '#10B981' }}>{payment.amount} ETB</Text>
                    </View>
                    {/* Period + Bank */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Icon name="calendar-today" size={13} color={colors.adminMuted} />
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>
                          {payment.paymentPeriod === 'monthly' ? 'Monthly' : 'Yearly'}
                        </Text>
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                        <Icon name="account-balance" size={13} color={colors.adminMuted} />
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{payment.bankName}</Text>
                      </View>
                    </View>
                    {/* Reference */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.adminCardAltBg, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 7 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, marginRight: 8 }}>
                        <Icon name="tag" size={13} color={colors.adminMuted} />
                        <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: colors.sidebarText, flex: 1 }} numberOfLines={1}>{payment.paymentReference}</Text>
                      </View>
                      <TouchableOpacity
                        onPress={() => handleCopyReference(payment.paymentReference)}
                        style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: copiedReference === payment.paymentReference ? '#10B981' : colors.adminBorder, justifyContent: 'center', alignItems: 'center' }}
                      >
                        <Icon name={copiedReference === payment.paymentReference ? 'check' : 'content-copy'} size={13} color={copiedReference === payment.paymentReference ? '#FFFFFF' : colors.adminMuted} />
                      </TouchableOpacity>
                    </View>
                    {/* Date */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Icon name="schedule" size={13} color={colors.adminMuted} />
                      <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>{formatDate(payment.createdAt)}</Text>
                    </View>
                  </View>

                  {/* Actions (pending only) */}
                  {payment.status === 'pending' && (
                    <View style={{ flexDirection: 'row', gap: 8, padding: 12, paddingTop: 0 }}>
                      <TouchableOpacity
                        onPress={() => handleConfirmPayment(payment.id, payment)}
                        disabled={processingPayment === payment.id}
                        style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, borderRadius: 10, backgroundColor: '#10B981', opacity: processingPayment === payment.id ? 0.6 : 1 }}
                      >
                        {processingPayment === payment.id
                          ? <ActivityIndicator size="small" color="#FFFFFF" />
                          : <Icon name="check-circle" size={15} color="#FFFFFF" />}
                        <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: '#FFFFFF' }}>Confirm</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => handleRejectPayment(payment.id, payment)}
                        disabled={processingPayment === payment.id}
                        style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, borderRadius: 10, backgroundColor: `${colors.danger}20`, borderWidth: 1, borderColor: '#EF4444', opacity: processingPayment === payment.id ? 0.6 : 1 }}
                      >
                        <Icon name="cancel" size={15} color="#EF4444" />
                        <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: '#EF4444' }}>Reject</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.adminCardBg, borderRadius: 12, padding: 12, marginTop: 4 }}>
                <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>
                  Page {currentPage} of {totalPages}
                </Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity
                    onPress={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 9, backgroundColor: currentPage === 1 ? colors.adminBorder : '#10B981' }}
                  >
                    <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, color: currentPage === 1 ? '#64748B' : '#FFFFFF' }}>← Prev</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 9, backgroundColor: currentPage === totalPages ? colors.adminBorder : '#10B981' }}
                  >
                    <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, color: currentPage === totalPages ? '#64748B' : '#FFFFFF' }}>Next →</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        ) : (
          /* ── Desktop Table ── */
          <View style={{ marginHorizontal: 20, marginBottom: 20, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.adminCardBg, ...Platform.select({ web: { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 3 } }) }}>
            <View style={Platform.select({ web: { overflowX: 'auto', width: '100%' } as any, default: {} })}>
              <View style={{ minWidth: 1100, flex: 1 }}>
                {/* Table Header */}
                <View style={{ flexDirection: 'row', backgroundColor: '#1E293B', paddingVertical: 11, paddingHorizontal: 14 }}>
                  {[
                    { label: 'User', flex: 1.5 },
                    { label: 'Subscription', flex: 1 },
                    { label: 'Amount', flex: 0.8 },
                    { label: 'Period', flex: 0.7 },
                    { label: 'Bank', flex: 0.9 },
                    { label: 'Reference', flex: 1.2 },
                    { label: 'Submitted', flex: 1 },
                    { label: 'Status', flex: 0.8 },
                    { label: 'Actions', flex: 0.7 }
                  ].map((col, i) => (
                    <View key={i} style={{ flex: col.flex, paddingHorizontal: 4 }}>
                      <Text style={{ fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6 }}>{col.label}</Text>
                    </View>
                  ))}
                </View>
                {/* Table Body */}
                <ScrollView style={{ maxHeight: 520 }} showsVerticalScrollIndicator={false}>
                  {filteredPayments.map((payment, idx) => (
                    <View key={payment.id} style={{ flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 14, alignItems: 'center', backgroundColor: isDarkMode ? (idx % 2 === 0 ? '#111827' : '#161F2E') : (idx % 2 === 0 ? '#F9FAFB' : '#FFFFFF'), borderBottomWidth: idx === filteredPayments.length - 1 ? 0 : 1, borderBottomColor: colors.adminBorder }}>
                      <View style={{ flex: 1.5, paddingHorizontal: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.sidebarTextActive }}>{payment.userName}</Text>
                        <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted, marginTop: 1 }}>{payment.userEmail}</Text>
                        <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>{payment.userPhone}</Text>
                      </View>
                      <View style={{ flex: 1, paddingHorizontal: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{payment.subscriptionName}</Text>
                      </View>
                      <View style={{ flex: 0.8, paddingHorizontal: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: '#10B981' }}>{payment.amount} ETB</Text>
                      </View>
                      <View style={{ flex: 0.7, paddingHorizontal: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{payment.paymentPeriod === 'monthly' ? 'Monthly' : 'Yearly'}</Text>
                      </View>
                      <View style={{ flex: 0.9, paddingHorizontal: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{payment.bankName}</Text>
                      </View>
                      <View style={{ flex: 1.2, paddingHorizontal: 4 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: colors.sidebarTextActive, flex: 1 }} numberOfLines={1}>{payment.paymentReference}</Text>
                          <TouchableOpacity onPress={() => handleCopyReference(payment.paymentReference)} style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: copiedReference === payment.paymentReference ? '#10B981' : colors.adminBorder, justifyContent: 'center', alignItems: 'center' }}>
                            <Icon name={copiedReference === payment.paymentReference ? 'check' : 'content-copy'} size={13} color={copiedReference === payment.paymentReference ? '#FFFFFF' : colors.adminMuted} />
                          </TouchableOpacity>
                        </View>
                      </View>
                      <View style={{ flex: 1, paddingHorizontal: 4 }}>
                        <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>{formatDate(payment.createdAt)}</Text>
                      </View>
                      <View style={{ flex: 0.8, paddingHorizontal: 4 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: `${getStatusColor(payment.status)}20`, alignSelf: 'flex-start' }}>
                          <Icon name={getStatusIcon(payment.status)} size={12} color={getStatusColor(payment.status)} />
                          <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: getStatusColor(payment.status), textTransform: 'uppercase' }}>{payment.status}</Text>
                        </View>
                      </View>
                      <View style={{ flex: 0.7, paddingHorizontal: 4 }}>
                        {payment.status === 'pending' ? (
                          <View style={{ flexDirection: 'row', gap: 6 }}>
                            <TouchableOpacity onPress={() => handleConfirmPayment(payment.id, payment)} disabled={processingPayment === payment.id} style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: '#10B981', justifyContent: 'center', alignItems: 'center', opacity: processingPayment === payment.id ? 0.5 : 1 }}>
                              {processingPayment === payment.id ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Icon name="check" size={16} color="#FFFFFF" />}
                            </TouchableOpacity>
                            <TouchableOpacity onPress={() => handleRejectPayment(payment.id, payment)} disabled={processingPayment === payment.id} style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: '#EF4444', justifyContent: 'center', alignItems: 'center', opacity: processingPayment === payment.id ? 0.5 : 1 }}>
                              <Icon name="close" size={16} color="#FFFFFF" />
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>-</Text>
                        )}
                      </View>
                    </View>
                  ))}
                </ScrollView>
              </View>
            </View>

            {/* Desktop Pagination */}
            {totalPages > 0 && (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, backgroundColor: colors.sidebarHover, borderTopWidth: 1, borderTopColor: colors.adminBorder }}>
                <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>
                  {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <TouchableOpacity onPress={() => handlePageChange(1)} disabled={currentPage === 1} style={{ width: 28, height: 28, borderRadius: 7, justifyContent: 'center', alignItems: 'center', backgroundColor: currentPage === 1 ? 'transparent' : colors.adminBorder }}>
                    <Icon name="first-page" size={16} color={currentPage === 1 ? '#475569' : colors.adminMuted} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, backgroundColor: currentPage === 1 ? colors.adminBorder : '#10B981' }}>
                    <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: currentPage === 1 ? '#64748B' : '#FFFFFF' }}>Prev</Text>
                  </TouchableOpacity>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) { pageNum = i + 1; }
                    else if (currentPage <= 3) { pageNum = i + 1; }
                    else if (currentPage >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                    else { pageNum = currentPage - 2 + i; }
                    return (
                      <TouchableOpacity key={pageNum} onPress={() => handlePageChange(pageNum)} style={{ width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center', backgroundColor: currentPage === pageNum ? '#10B981' : 'transparent' }}>
                        <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: currentPage === pageNum ? '#FFFFFF' : colors.adminMuted }}>{pageNum}</Text>
                      </TouchableOpacity>
                    );
                  })}
                  <TouchableOpacity onPress={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, backgroundColor: currentPage === totalPages ? colors.adminBorder : '#10B981' }}>
                    <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: currentPage === totalPages ? '#64748B' : '#FFFFFF' }}>Next</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} style={{ width: 28, height: 28, borderRadius: 7, justifyContent: 'center', alignItems: 'center', backgroundColor: currentPage === totalPages ? 'transparent' : colors.adminBorder }}>
                    <Icon name="last-page" size={16} color={currentPage === totalPages ? '#475569' : colors.adminMuted} />
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

// Styles removed — all styling is inline

export default PaymentRequestsContent;


